import { useEffect, useRef, useCallback, useMemo } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useGetUser } from "../api/queries/useGetUser";
import { useAppContext } from "../Context/AppContext";
import { Spinner } from "react-bootstrap";
import { useSaveUser } from "../api/mutations/useSaveUser";
import { isLoggedIn } from "../auth/authStorage";
import { explorerOnboardingQueryKey } from "../auth/authSession";
import { getMyExplorerOnboarding } from "../api/explorerOnboardingApi";
import { hasExplorerOnboarding as checkExplorerOnboarding } from "../explorer/hasExplorerOnboarding";
import { shouldResumeEarlyStageRegistration } from "../utils/earlyStageRegistrationGate";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ALLOWED_PATHS = [
  "/register-user",
  "/membership-inactive",
  "/manage-membership",
  "/renew-membership",
  "/event-booking",
  "/event-questions",
  "/confirm-booking",
  "/explorer",
];

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = isLoggedIn();
  const location = useLocation();
  const { objectId, setUser } = useAppContext();
  const navigate = useNavigate();
  const hasUpdatedRef = useRef(false);

  const handleUpdateSuccess = useCallback(() => {
    if (objectId) {
      localStorage.setItem(`lastLoginUpdated_${objectId}`, "true");
    }
  }, [objectId]);

  const { mutate: updateUser } = useSaveUser({
    onSuccess: handleUpdateSuccess,
  });

  const { data: userData, isLoading, isError, error } = useGetUser({
    id: objectId || "",
    enabled: !!objectId,
  });

  const { data: explorerMe, isLoading: explorerMeLoading } = useQuery({
    queryKey: explorerOnboardingQueryKey(objectId),
    queryFn: getMyExplorerOnboarding,
    enabled: isAuthenticated && !!objectId,
    retry: false,
    staleTime: 60_000,
  });

  const hasExplorerOnboarding = useMemo(
    () => checkExplorerOnboarding(explorerMe),
    [explorerMe]
  );

  const isExplorerHomePath = useMemo(() => {
    const p = location.pathname;
    return (
      p === "/dashboard" ||
      p.startsWith("/event-booking") ||
      p.startsWith("/event-questions") ||
      p.startsWith("/confirm-booking")
    );
  }, [location.pathname]);

  const isExplorerCheckoutPath = useMemo(
    () =>
      location.pathname === "/explorer/checkout" ||
      location.pathname.startsWith("/explorer/checkout/"),
    [location.pathname]
  );

  useEffect(() => {
    if (userData) {
      setUser(userData);
    }
  }, [userData, setUser]);

  useEffect(() => {
    if (hasUpdatedRef.current || !userData?.user?.id || !objectId) {
      return;
    }

    const storageKey = `lastLoginUpdated_${objectId}`;
    const alreadyUpdated = localStorage.getItem(storageKey);

    if (!alreadyUpdated) {
      hasUpdatedRef.current = true;

      const formData = new FormData();
      const now = new Date().toISOString();

      const updatedData = {
        ...userData.user,
        lastLoggedIn: now,
      };

      Object.entries(updatedData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (typeof value === "object") {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      updateUser(formData);
    }
  }, [objectId, updateUser, userData]);

  const isOnAllowedPath = useMemo(() => {
    return ALLOWED_PATHS.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  }, [location.pathname]);

  const isCancelledMembership = useMemo(() => {
    const userStatus = userData?.user?.status;
    if (userStatus === "InActive") return true;
    const stripeStatus = userData?.user?.stripeStatus?.toLowerCase?.();
    return stripeStatus === "canceled" || stripeStatus === "cancelled";
  }, [userData?.user?.stripeStatus, userData?.user?.status]);

  const shouldForceEarlyStageResume = useMemo(() => {
    return shouldResumeEarlyStageRegistration(null, userData?.user);
  }, [userData?.user]);

  const isMissingUserProfile = useMemo(() => {
    const msg =
      (error as any)?.response?.data?.message ||
      (error as any)?.response?.data ||
      (error as any)?.message;
    return (
      isError &&
      (msg === "User does not exist" ||
        (typeof msg === "string" && msg.includes("User does not exist")))
    );
  }, [error, isError]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || isOnAllowedPath) {
      return;
    }

    if (isMissingUserProfile) {
      navigate("/register-user", { replace: true });
      return;
    }

    if (!userData) return;

    // Any unpaid early-stage verification flow (pending/rejected/price-plan selection/not-submitted)
    // must stay on registration, even for explorer-origin users.
    if (shouldForceEarlyStageResume && location.pathname !== "/register-user") {
      navigate("/register-user", { replace: true });
      return;
    }

    // Existing Explorer users should not re-enter checkout on refresh/login.
    if (isExplorerCheckoutPath) {
      if (explorerMeLoading) return;
      if (hasExplorerOnboarding) {
        navigate("/event-booking", { replace: true });
      }
      return;
    }

    // Explorer users can access event pages but not the member dashboard.
    if (
      location.pathname === "/dashboard" &&
      userData.user?.hasPaid !== 1 &&
      hasExplorerOnboarding
    ) {
      navigate("/members-benefits-smbf", { replace: true });
      return;
    }

    if (isCancelledMembership) {
      navigate("/membership-inactive", { replace: true });
      return;
    }

    if (userData.user?.hasPaid !== 1) {
      if (isExplorerHomePath && explorerMeLoading) {
        return;
      }
      if (hasExplorerOnboarding && isExplorerHomePath) {
        return;
      }
      navigate("/register-user", { replace: true });
    }
  }, [
    isAuthenticated,
    isLoading,
    userData,
    isOnAllowedPath,
    isCancelledMembership,
    isMissingUserProfile,
    hasExplorerOnboarding,
    shouldForceEarlyStageResume,
    isExplorerHomePath,
    explorerMeLoading,
    location.pathname,
    isExplorerCheckoutPath,
    navigate,
  ]);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const needsExplorerGate =
    !!userData?.user &&
    userData.user.hasPaid !== 1 &&
    !isOnAllowedPath &&
    isExplorerHomePath;

  if (
    (isLoading && !userData) ||
    (needsExplorerGate && explorerMeLoading) ||
    (isExplorerCheckoutPath && explorerMeLoading)
  ) {
    return (
      <div
        className="d-flex justify-content-center align-items-center vh-100"
        style={{ minHeight: "100vh" }}
      >
        <Spinner animation="border" role="status" variant="primary" />
      </div>
    );
  }

  if (isMissingUserProfile && !isOnAllowedPath) {
    return (
      <div
        className="d-flex justify-content-center align-items-center vh-100"
        style={{ minHeight: "100vh" }}
      >
        <Spinner animation="border" role="status" variant="primary" />
      </div>
    );
  }

  const unpaidMustCompleteRegistration =
    location.pathname === "/dashboard" &&
    !isOnAllowedPath &&
    !!userData?.user &&
    userData.user.hasPaid !== 1 &&
    !explorerMeLoading &&
    !hasExplorerOnboarding;

  if (unpaidMustCompleteRegistration) {
    return (
      <div
        className="d-flex justify-content-center align-items-center vh-100"
        style={{ minHeight: "100vh" }}
      >
        <Spinner animation="border" role="status" variant="primary" />
      </div>
    );
  }

  return <>{children}</>;
}

ProtectedRoute.displayName = "ProtectedRoute";

export default ProtectedRoute;
